// Utilitário para otimizar comportamento quando usuário troca de abas
// Resolve problemas de loading infinito e performance

import React from 'react';

class TabFocusOptimizer {
  private static instance: TabFocusOptimizer;
  private isTabActive = true;
  private listeners: Array<(isActive: boolean) => void> = [];
  private debounceTimeout: NodeJS.Timeout | null = null;
  private readonly DEBOUNCE_DELAY = 1000; // 1 segundo de debounce

  private constructor() {
    this.initializeListeners();
  }

  public static getInstance(): TabFocusOptimizer {
    if (!TabFocusOptimizer.instance) {
      TabFocusOptimizer.instance = new TabFocusOptimizer();
    }
    return TabFocusOptimizer.instance;
  }

  private initializeListeners() {
    // Listener para mudanças de visibilidade da página
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Listeners para foco da janela (fallback)
    window.addEventListener('focus', this.handleWindowFocus.bind(this));
    window.addEventListener('blur', this.handleWindowBlur.bind(this));
  }

  private handleVisibilityChange() {
    const isActive = !document.hidden;
    this.updateTabState(isActive);
  }

  private handleWindowFocus() {
    this.updateTabState(true);
  }

  private handleWindowBlur() {
    this.updateTabState(false);
  }

  private updateTabState(isActive: boolean) {
    // Evitar atualizações desnecessárias
    if (this.isTabActive === isActive) {
      return;
    }

    this.isTabActive = isActive;

    // Debounce para evitar múltiplas chamadas rápidas
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.notifyListeners(isActive);
      this.debounceTimeout = null;
    }, this.DEBOUNCE_DELAY);
  }

  private notifyListeners(isActive: boolean) {
    this.listeners.forEach(listener => {
      try {
        listener(isActive);
      } catch (error) {
        console.error('Erro ao notificar listener de foco da aba:', error);
      }
    });
  }

  // Método público para adicionar listeners
  public addListener(callback: (isActive: boolean) => void): () => void {
    this.listeners.push(callback);
    
    // Retorna função para remover o listener
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Método público para verificar se a aba está ativa
  public isActive(): boolean {
    return this.isTabActive;
  }

  // Método para limpar todos os listeners (útil para testes)
  public cleanup() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('focus', this.handleWindowFocus.bind(this));
    window.removeEventListener('blur', this.handleWindowBlur.bind(this));
    
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    
    this.listeners = [];
  }
}

// Hook React para usar o otimizador de foco de aba
export const useTabFocus = (callback?: (isActive: boolean) => void) => {
  const optimizer = TabFocusOptimizer.getInstance();
  
  React.useEffect(() => {
    if (!callback) return;
    
    const removeListener = optimizer.addListener(callback);
    return removeListener;
  }, [callback, optimizer]);
  
  return {
    isTabActive: optimizer.isActive(),
    addListener: optimizer.addListener.bind(optimizer)
  };
};

// Exportar instância singleton
export const tabFocusOptimizer = TabFocusOptimizer.getInstance();
export default TabFocusOptimizer;